// ============================================================
// src/pages/VehicleV2/BatteryCardMaintenanceEntry.jsx
// BATTERY CARD MAINTENANCE ENTRY
//
// Backend:
// app.use(
//   "/api/batteryCardMaintenanceEntry",
//   batteryCardMaintenanceEntry
// );
// ============================================================

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaUndo,
} from "react-icons/fa";

// ============================================================
// API
// ============================================================

const API_BASE =
  "http://nissi-india.com/api/batteryCardMaintenanceEntry";

// ============================================================
// HELPERS
// ============================================================

const toInt = (value, defaultValue = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? Math.trunc(number)
    : defaultValue;
};

const toStr = (value, defaultValue = "") => {
  if (value === undefined || value === null) {
    return defaultValue;
  }

  return String(value).trim();
};

// ============================================================
// DATE HELPER
//
// Supports:
// 2026-09-03
// 03/09/2026
// 03-09-2026
// Date object
//
// Returns:
// yyyy-mm-dd
// ============================================================

const toISODate = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return "";
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return "";
  }

  // Already yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return stringValue;
  }

  // dd/mm/yyyy
  // dd-mm-yyyy
  const match = stringValue.match(
    /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/
  );

  if (match) {
    const day = String(match[1]).padStart(2, "0");
    const month = String(match[2]).padStart(2, "0");
    const year = match[3];

    return `${year}-${month}-${day}`;
  }

  // Fallback
  const date = new Date(stringValue);

  if (!Number.isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  return "";
};

// ============================================================
// EMPTY FORM
// ============================================================

const emptyForm = {
  storeId: "",
  vehicleId: "",
  vehicleCode: "",
  vehicleName: "",
  vehicleRegNo: "",

  batteryTypeText: "",
  batteryId: "",
  batterySerialId: "",

  purchaseDate: "",
  fittedDate: "",
};

// ============================================================
// COMPONENT
// ============================================================

const BatteryCardMaintenanceEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);

  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // ==========================================================
  // LOCAL STORAGE / SESSION STORAGE
  // ==========================================================

  const finYearId =
    localStorage.getItem("finYearId") || "";

  const finYear =
    localStorage.getItem("finYear") || "";

  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";

  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const loginUserId =
    sessionStorage.getItem("loginUserId") ||
    userId ||
    "";

  // ==========================================================
  // COMMON HEADERS
  // ==========================================================

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    ]
  );

  // ==========================================================
  // MAINTENANCE ID
  // ==========================================================

  const maintenanceId = useMemo(() => {
    return toInt(
      searchParams.get("batteryCardMainId") ||
        searchParams.get("maintenanceId") ||
        searchParams.get("batterycardmainid") ||
        searchParams.get("id"),
      0
    );
  }, [searchParams]);

  const isEdit = maintenanceId > 0;

  // ==========================================================
  // STATE
  // ==========================================================

  const [stores, setStores] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [batteryTypes, setBatteryTypes] = useState([]);
  const [batteryCodes, setBatteryCodes] = useState([]);
  const [batterySerials, setBatterySerials] = useState([]);

  const [branchName, setBranchName] = useState("");

  const [form, setForm] = useState(emptyForm);

  const [pageLoading, setPageLoading] = useState(false);
  const [loadingDDL, setLoadingDDL] = useState(false);
  const [saving, setSaving] = useState(false);

  // ==========================================================
  // INPUT STYLE
  // ==========================================================

  const inputClass = useMemo(
    () =>
      `w-full h-10 px-3 border rounded-md
       focus:ring-2 focus:ring-blue-500
       focus:border-transparent
       ${
         isDark
           ? "bg-gray-700 border-gray-600 text-white"
           : "bg-white border-gray-300 text-gray-900"
       }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `text-sm font-medium whitespace-nowrap ${
        isDark
          ? "text-gray-200"
          : "text-gray-700"
      }`,
    [isDark]
  );

  // ==========================================================
  // SET FIELD
  // ==========================================================

  const setField = (name, value) => {
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // ==========================================================
  // FILL EDIT FORM
  // ==========================================================

  const fillFormFromEdit = useCallback(
    (
      editData,
      allVehicles = [],
      defaultStoreId = ""
    ) => {
      const vehicleId = toStr(
        editData?.vehicleId ??
          editData?.fld_vehicleid ??
          editData?.vehicleid
      );

      const batteryId = toStr(
        editData?.batteryId ??
          editData?.fld_batteryid ??
          editData?.batteryid ??
          editData?.batteryCodeId
      );

      const batterySerialId = toStr(
        editData?.batterySerialId ??
          editData?.serialId ??
          editData?.batteryserialid ??
          batteryId
      );

      const matchedVehicle =
        allVehicles.find(
          (vehicle) =>
            String(vehicle.id) ===
            String(vehicleId)
        );

      setForm({
        storeId: toStr(
          editData?.storeId ??
            editData?.fld_storeid ??
            editData?.storeid ??
            defaultStoreId
        ),

        vehicleId,

        vehicleCode: toStr(
          editData?.vehicleCode ??
            matchedVehicle?.code ??
            ""
        ),

        vehicleName: toStr(
          editData?.vehicleName ??
            matchedVehicle?.name ??
            ""
        ),

        vehicleRegNo: toStr(
          editData?.vehicleRegNo ??
            editData?.vehicleRegno ??
            matchedVehicle?.regNo ??
            ""
        ),

        batteryTypeText: toStr(
          editData?.batteryTypeText ??
            editData?.batteryTypeMain ??
            editData?.batterytype ??
            ""
        ),

        batteryId,

        batterySerialId,

        purchaseDate: toISODate(
          editData?.purchaseDate ??
            editData?.purchasedDate ??
            ""
        ),

        fittedDate: toISODate(
          editData?.fittedDate ??
            ""
        ),
      });
    },
    []
  );

  // ==========================================================
  // LOAD VEHICLE DEPENDENT DATA
  // ==========================================================

  const loadVehicleDependent = useCallback(
    async (
      selectedBranchId,
      selectedVehicleId,
      selectedMaintenanceId = 0
    ) => {
      try {
        if (
          !selectedBranchId ||
          !selectedVehicleId
        ) {
          setBatteryTypes([]);
          setBatteryCodes([]);
          setBatterySerials([]);
          return;
        }

        setLoadingDDL(true);

        const response = await axios.get(
          `${API_BASE}/vehicle`,
          {
            params: {
              branchId: toInt(
                selectedBranchId,
                0
              ),

              vehicleId: toInt(
                selectedVehicleId,
                0
              ),

              maintenanceId: toInt(
                selectedMaintenanceId,
                0
              ),
            },

            headers: commonHeaders,
          }
        );

        const data = response?.data;

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Failed to load vehicle data"
          );
        }

        setBatteryTypes(
          Array.isArray(
            data?.data?.batteryTypes
          )
            ? data.data.batteryTypes
            : []
        );

        setBatteryCodes(
          Array.isArray(
            data?.data?.batteryCodes
          )
            ? data.data.batteryCodes
            : []
        );

        setBatterySerials(
          Array.isArray(
            data?.data?.batterySerials
          )
            ? data.data.batterySerials
            : []
        );
      } catch (error) {
        console.error(
          "loadVehicleDependent:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load battery details"
        );

        setBatteryTypes([]);
        setBatteryCodes([]);
        setBatterySerials([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  // ==========================================================
  // LOAD PURCHASE DATE + BATTERY TYPE
  //
  // Backend expected:
  //
  // {
  //   success: true,
  //   data: {
  //     batteryId: 3047,
  //     batteryType: "test",
  //     purchaseDate: "01/04/2023"
  //   }
  // }
  // ==========================================================

  const loadPurchaseDate = useCallback(
    async (batteryId) => {
      try {
        if (!batteryId) {
          setForm((previous) => ({
            ...previous,
            purchaseDate: "",
            batteryTypeText: "",
          }));

          return;
        }

        setLoadingDDL(true);

        const response = await axios.get(
          `${API_BASE}/battery/purchase-date`,
          {
            params: {
              batteryId: toInt(
                batteryId,
                0
              ),
            },

            headers: commonHeaders,
          }
        );

        const data = response?.data;

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Failed to load battery details"
          );
        }

        const returnedBatteryType =
          toStr(
            data?.data?.batteryType ??
              data?.data?.batterytype ??
              data?.data?.fld_batterytype ??
              ""
          );

        const returnedPurchaseDate =
          toISODate(
            data?.data?.purchaseDate ??
              data?.data?.purchasedate ??
              ""
          );

        // ------------------------------------------------------
        // Update form
        // ------------------------------------------------------

        setForm((previous) => ({
          ...previous,

          batteryId: String(
            batteryId
          ),

          batterySerialId:
            String(batteryId),

          batteryTypeText:
            returnedBatteryType,

          purchaseDate:
            returnedPurchaseDate,
        }));

        // ------------------------------------------------------
        // Add returned battery type if not available
        // ------------------------------------------------------

        if (returnedBatteryType) {
          setBatteryTypes((previous) => {
            const exists = previous.some(
              (item) =>
                String(
                  item?.type || ""
                ).trim() ===
                returnedBatteryType
            );

            if (exists) {
              return previous;
            }

            return [
              ...previous,
              {
                id: `selected-${batteryId}`,
                type: returnedBatteryType,
              },
            ];
          });
        }
      } catch (error) {
        console.error(
          "loadPurchaseDate:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load battery details"
        );

        setForm((previous) => ({
          ...previous,
          purchaseDate: "",
          batteryTypeText: "",
        }));
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  const loadInit = useCallback(
    async () => {
      try {
        if (!branchId) {
          toast.error(
            "branchId not found"
          );

          return;
        }

        setPageLoading(true);

        const response = await axios.get(
          `${API_BASE}/init`,
          {
            params: {
              branchId: toInt(
                branchId,
                0
              ),

              loginUserId: toInt(
                loginUserId,
                0
              ),

              maintenanceId: isEdit
                ? maintenanceId
                : 0,
            },

            headers: commonHeaders,
          }
        );

        const data = response?.data;

        if (!data?.success) {
          throw new Error(
            data?.message ||
              "Failed to load initial data"
          );
        }

        const d = data?.data || {};

        const initStores =
          Array.isArray(d?.stores)
            ? d.stores
            : [];

        const initVehicles =
          Array.isArray(d?.vehicles)
            ? d.vehicles
            : [];

        const initBatteryTypes =
          Array.isArray(
            d?.batteryTypes
          )
            ? d.batteryTypes
            : [];

        const initBatteryCodes =
          Array.isArray(
            d?.batteryCodes
          )
            ? d.batteryCodes
            : [];

        const initBatterySerials =
          Array.isArray(
            d?.batterySerials
          )
            ? d.batterySerials
            : [];

        // ------------------------------------------------------
        // Set initial data
        // ------------------------------------------------------

        setBranchName(
          toStr(
            d?.branchName
          )
        );

        setStores(
          initStores
        );

        setVehicles(
          initVehicles
        );

        setBatteryTypes(
          initBatteryTypes
        );

        setBatteryCodes(
          initBatteryCodes
        );

        setBatterySerials(
          initBatterySerials
        );

        const defaultStoreId =
          toStr(
            initStores?.[0]?.id
          );

        // ------------------------------------------------------
        // EDIT MODE
        // ------------------------------------------------------

        if (d?.editData) {
          fillFormFromEdit(
            d.editData,
            initVehicles,
            defaultStoreId
          );

          const editVehicleId =
            toStr(
              d.editData?.vehicleId ??
                d.editData
                  ?.fld_vehicleid ??
                d.editData?.vehicleid
            );

          const editBatteryId =
            toStr(
              d.editData?.batteryId ??
                d.editData
                  ?.fld_batteryid ??
                d.editData?.batteryid ??
                d.editData
                  ?.batteryCodeId
            );

          // ----------------------------------------------------
          // Load vehicle-dependent battery data
          // ----------------------------------------------------

          if (editVehicleId) {
            await loadVehicleDependent(
              branchId,
              editVehicleId,
              maintenanceId
            );
          }

          // ----------------------------------------------------
          // Load selected battery details
          // ----------------------------------------------------

          if (editBatteryId) {
            await loadPurchaseDate(
              editBatteryId
            );
          }
        } else {
          // ----------------------------------------------------
          // ADD MODE
          // ----------------------------------------------------

          setForm({
            ...emptyForm,

            storeId:
              defaultStoreId,
          });
        }
      } catch (error) {
        console.error(
          "loadInit:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load initial data"
        );
      } finally {
        setPageLoading(false);
      }
    },
    [
      branchId,
      loginUserId,
      isEdit,
      maintenanceId,
      commonHeaders,
      fillFormFromEdit,
      loadVehicleDependent,
      loadPurchaseDate,
    ]
  );

  // ==========================================================
  // INIT EFFECT
  // ==========================================================

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  // ==========================================================
  // VEHICLE CHANGE
  // ==========================================================

  const onVehicleById = async (
    vehicleId
  ) => {
    const selectedVehicleId =
      String(vehicleId || "");

    const matchedVehicle =
      vehicles.find(
        (vehicle) =>
          String(vehicle.id) ===
          selectedVehicleId
      );

    // --------------------------------------------------------
    // Reset battery-related fields
    // --------------------------------------------------------

    setForm((previous) => ({
      ...previous,

      vehicleId:
        selectedVehicleId,

      vehicleCode:
        matchedVehicle?.code || "",

      vehicleName:
        matchedVehicle?.name || "",

      vehicleRegNo:
        matchedVehicle?.regNo || "",

      batteryTypeText: "",
      batteryId: "",
      batterySerialId: "",
      purchaseDate: "",
    }));

    setBatteryTypes([]);
    setBatteryCodes([]);
    setBatterySerials([]);

    if (!selectedVehicleId) {
      return;
    }

    await loadVehicleDependent(
      branchId,
      selectedVehicleId,
      maintenanceId
    );
  };

  // ==========================================================
  // VEHICLE CODE CHANGE
  // ==========================================================

  const onVehicleCodeChange = async (
    event
  ) => {
    const code =
      event.target.value;

    const matchedVehicle =
      vehicles.find(
        (vehicle) =>
          String(vehicle.code) ===
          String(code)
      );

    await onVehicleById(
      matchedVehicle?.id || ""
    );
  };

  // ==========================================================
  // VEHICLE NAME CHANGE
  // ==========================================================

  const onVehicleNameChange = async (
    event
  ) => {
    const name =
      event.target.value;

    const matchedVehicle =
      vehicles.find(
        (vehicle) =>
          String(vehicle.name) ===
          String(name)
      );

    await onVehicleById(
      matchedVehicle?.id || ""
    );
  };

  // ==========================================================
  // VEHICLE REG NO CHANGE
  // ==========================================================

  const onVehicleRegNoChange = async (
    event
  ) => {
    const regNo =
      event.target.value;

    const matchedVehicle =
      vehicles.find(
        (vehicle) =>
          String(vehicle.regNo) ===
          String(regNo)
      );

    await onVehicleById(
      matchedVehicle?.id || ""
    );
  };

  // ==========================================================
  // BATTERY CODE CHANGE
  // ==========================================================

  const onBatteryCodeChange = async (
    event
  ) => {
    const batteryId =
      event.target.value;

    // --------------------------------------------------------
    // Clear battery
    // --------------------------------------------------------

    if (!batteryId) {
      setForm((previous) => ({
        ...previous,

        batteryId: "",
        batterySerialId: "",
        batteryTypeText: "",
        purchaseDate: "",
      }));

      return;
    }

    // --------------------------------------------------------
    // Set battery ID immediately
    // --------------------------------------------------------

    setForm((previous) => ({
      ...previous,

      batteryId:
        String(batteryId),

      // Current backend mapping
      batterySerialId:
        String(batteryId),

      // Clear old values until API responds
      batteryTypeText: "",
      purchaseDate: "",
    }));

    // --------------------------------------------------------
    // Load battery type + purchase date
    // --------------------------------------------------------

    await loadPurchaseDate(
      batteryId
    );
  };

  // ==========================================================
  // BATTERY SERIAL CHANGE
  // ==========================================================

  const onBatterySerialChange = (
    event
  ) => {
    const serialId =
      event.target.value;

    setField(
      "batterySerialId",
      serialId
    );
  };

  // ==========================================================
  // BATTERY TYPE CHANGE
  // ==========================================================

  const onBatteryTypeChange = (
    event
  ) => {
    setField(
      "batteryTypeText",
      event.target.value
    );
  };

  // ==========================================================
  // CAN SAVE
  // ==========================================================

  const canSave = useMemo(() => {
    return Boolean(
      toStr(form.storeId) &&
        toStr(form.vehicleId) &&
        toStr(form.batteryId) &&
        toStr(form.batterySerialId) &&
        toStr(form.batteryTypeText) &&
        toStr(form.fittedDate)
    );
  }, [form]);

  // ==========================================================
  // VALIDATION
  // ==========================================================

  const validate = () => {
    if (!toStr(form.storeId)) {
      toast.error(
        "Please select Store"
      );

      return false;
    }

    if (!toStr(form.vehicleId)) {
      toast.error(
        "Please select Vehicle"
      );

      return false;
    }

    if (!toStr(form.batterySerialId)) {
      toast.error(
        "Please select Battery SerialNo"
      );

      return false;
    }

    if (!toStr(form.batteryId)) {
      toast.error(
        "Please select Battery Code"
      );

      return false;
    }

    if (!toStr(form.batteryTypeText)) {
      toast.error(
        "Please select Battery Type"
      );

      return false;
    }

    if (!toStr(form.fittedDate)) {
      toast.error(
        "Please select Fitted Date"
      );

      return false;
    }

    if (!toInt(branchId)) {
      toast.error(
        "branchId not found"
      );

      return false;
    }

    if (!toInt(loginUserId)) {
      toast.error(
        "loginUserId not found"
      );

      return false;
    }

    return true;
  };

  // ==========================================================
  // SAVE
  // ==========================================================

  const onSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);

      const payload = {
        maintenanceId:
          isEdit
            ? maintenanceId
            : 0,

        batteryCardMainId:
          isEdit
            ? maintenanceId
            : 0,

        branchId: toInt(
          branchId,
          0
        ),

        loginUserId: toInt(
          loginUserId,
          0
        ),

        storeId: toInt(
          form.storeId,
          0
        ),

        vehicleId: toInt(
          form.vehicleId,
          0
        ),

        batteryId: toInt(
          form.batteryId,
          0
        ),

        batterySerialId: toInt(
          form.batterySerialId,
          0
        ),

        batteryTypeText:
          toStr(
            form.batteryTypeText
          ),

        fittedDate:
          toStr(
            form.fittedDate
          ),

        branchType:
          branch,
      };

      console.log(
        "Battery Card Maintenance SAVE payload:",
        payload
      );

      const response =
        await axios.post(
          `${API_BASE}/save`,
          payload,
          {
            headers:
              commonHeaders,
          }
        );

      const data =
        response?.data;

      if (!data?.success) {
        throw new Error(
          data?.message ||
            "Save failed"
        );
      }

      toast.success(
        data?.message ||
          (isEdit
            ? "Updated successfully"
            : "Saved successfully")
      );

      setTimeout(() => {
        navigate(
          "/vehicle/BatteryCardMaintenanceListing"
        );
      }, 700);
    } catch (error) {
      console.error(
        "onSave:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // CLEAR / RELOAD
  // ==========================================================

  const onClear = () => {
    // --------------------------------------------------------
    // Edit mode -> reload original data
    // --------------------------------------------------------

    if (isEdit) {
      loadInit();
      return;
    }

    // --------------------------------------------------------
    // Add mode -> clear form
    // --------------------------------------------------------

    setForm({
      ...emptyForm,

      storeId:
        stores?.[0]?.id
          ? String(
              stores[0].id
            )
          : "",
    });

    setBatteryTypes([]);
    setBatteryCodes([]);
    setBatterySerials([]);
  };

  // ==========================================================
  // BACK
  // ==========================================================

  const onBack = () => {
    navigate(
      "/vehicle/BatteryCardMaintenanceListing"
    );
  };

  // ==========================================================
  // ROW COMPONENT
  // ==========================================================

  const Row = ({
    label,
    required = false,
    children,
  }) => {
    return (
      <div className="grid grid-cols-[170px_1fr] items-center gap-4">
        <div className={labelClass}>
          {label}

          {required ? (
            <span className="text-red-500">
              {" "}
              *
            </span>
          ) : null}
        </div>

        <div>
          {children}
        </div>
      </div>
    );
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark
            ? "bg-gray-900"
            : "bg-gray-100"
        }`}
      >
        {/* ====================================================
            HEADER BAR
            ==================================================== */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BATTERY CARD MAINTENANCE ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        {/* ====================================================
            MAIN CARD
            ==================================================== */}

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* ==================================================
              PAGE HEADER
              ================================================== */}

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1
                className={`text-2xl font-bold ${
                  isDark
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                {isEdit
                  ? "Edit Battery Card Maintenance"
                  : "Add Battery Card Maintenance"}
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isEdit
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {isEdit
                  ? `Edit Mode (#${maintenanceId})`
                  : "New Entry"}
              </span>
            </div>

            <div
              className={`text-sm ${
                isDark
                  ? "text-gray-200"
                  : "text-gray-600"
              }`}
            >
              INFRA SCREEN : B-15/1
            </div>
          </div>

          {/* ==================================================
              FORM CONTAINER
              ================================================== */}

          <div
            className={`rounded-xl p-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            {pageLoading ? (
              <div className="py-10 text-center">
                Loading...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-4">
                {/* ==========================================
                    LEFT COLUMN
                    ========================================== */}

                <div className="space-y-4">
                  {/* BRANCH */}

                  <Row label="Branch Name">
                    <input
                      value={
                        branchName || ""
                      }
                      className={
                        inputClass
                      }
                      disabled
                      readOnly
                    />
                  </Row>

                  {/* VEHICLE CODE */}

                  <Row
                    label="Vehicle Code"
                    required
                  >
                    <select
                      value={
                        form.vehicleCode ||
                        ""
                      }
                      onChange={
                        onVehicleCodeChange
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving ||
                        loadingDDL
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {vehicles.map(
                        (vehicle) => (
                          <option
                            key={
                              vehicle.id
                            }
                            value={
                              vehicle.code
                            }
                          >
                            {
                              vehicle.code
                            }
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* VEHICLE REG NO */}

                  <Row label="Vehicle RegNo">
                    <select
                      value={
                        form.vehicleRegNo ||
                        ""
                      }
                      onChange={
                        onVehicleRegNoChange
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving ||
                        loadingDDL
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {vehicles.map(
                        (vehicle) => (
                          <option
                            key={
                              vehicle.id
                            }
                            value={
                              vehicle.regNo
                            }
                          >
                            {
                              vehicle.regNo
                            }
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* BATTERY CODE */}

                  <Row
                    label="BatteryCode"
                    required
                  >
                    <select
                      value={
                        form.batteryId ||
                        ""
                      }
                      onChange={
                        onBatteryCodeChange
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving ||
                        loadingDDL ||
                        !form.vehicleId
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {batteryCodes.map(
                        (
                          battery,
                          index
                        ) => (
                          <option
                            key={
                              battery.id ??
                              index
                            }
                            value={
                              battery.id
                            }
                          >
                            {
                              battery.code
                            }
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* PURCHASE DATE */}

                  <Row label="PurchasedDate">
                    <input
                      type="date"
                      value={
                        form.purchaseDate ||
                        ""
                      }
                      className={
                        inputClass
                      }
                      disabled
                      readOnly
                    />
                  </Row>
                </div>

                {/* ==========================================
                    RIGHT COLUMN
                    ========================================== */}

                <div className="space-y-4">
                  {/* STORE */}

                  <Row
                    label="Store"
                    required
                  >
                    <select
                      value={
                        form.storeId
                      }
                      onChange={(event) =>
                        setField(
                          "storeId",
                          event.target.value
                        )
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {stores.map(
                        (store) => (
                          <option
                            key={
                              store.id
                            }
                            value={
                              store.id
                            }
                          >
                            {store.name ||
                              store.code}
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* VEHICLE NAME */}

                  <Row
                    label="Vehicle Name"
                    required
                  >
                    <select
                      value={
                        form.vehicleName ||
                        ""
                      }
                      onChange={
                        onVehicleNameChange
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving ||
                        loadingDDL
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {vehicles.map(
                        (vehicle) => (
                          <option
                            key={
                              vehicle.id
                            }
                            value={
                              vehicle.name
                            }
                          >
                            {
                              vehicle.name
                            }
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* BATTERY SERIAL */}

                  <Row
                    label="BatterySerialNo"
                    required
                  >
                    <select
                      value={
                        form.batterySerialId ||
                        ""
                      }
                      onChange={
                        onBatterySerialChange
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving ||
                        loadingDDL ||
                        !form.vehicleId
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {batterySerials.map(
                        (
                          battery,
                          index
                        ) => (
                          <option
                            key={
                              battery.id ??
                              index
                            }
                            value={
                              battery.id
                            }
                          >
                            {
                              battery.serialNo
                            }
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* BATTERY TYPE */}

                  <Row
                    label="BatteryType"
                    required
                  >
                    <select
                      value={
                        form.batteryTypeText ||
                        ""
                      }
                      onChange={
                        onBatteryTypeChange
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving ||
                        loadingDDL ||
                        !form.vehicleId
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {batteryTypes.map(
                        (
                          batteryType,
                          index
                        ) => (
                          <option
                            key={
                              batteryType.id ??
                              index
                            }
                            value={
                              batteryType.type
                            }
                          >
                            {
                              batteryType.type
                            }
                          </option>
                        )
                      )}
                    </select>
                  </Row>

                  {/* FITTED DATE */}

                  <Row
                    label="FittedDate"
                    required
                  >
                    <input
                      type="date"
                      value={
                        form.fittedDate ||
                        ""
                      }
                      onChange={(event) =>
                        setField(
                          "fittedDate",
                          event.target.value
                        )
                      }
                      className={
                        inputClass
                      }
                      disabled={
                        saving
                      }
                    />
                  </Row>
                </div>
              </div>
            )}

            {/* ==================================================
                HELP TEXT
                ================================================== */}

            {!pageLoading ? (
              <div className="mt-8">
                <div
                  className={`text-sm ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Select vehicle first. After selecting
                  BatteryCode, the Battery Type and
                  Purchased Date will automatically load
                  from the selected battery.
                </div>
              </div>
            ) : null}

            {/* ==================================================
                BUTTONS
                ================================================== */}

            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              {/* CLEAR / RELOAD */}

              <button
                type="button"
                onClick={onClear}
                disabled={
                  saving ||
                  pageLoading
                }
                className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
              >
                {isEdit ? (
                  <FaUndo className="w-4 h-4" />
                ) : (
                  <FaTimes className="w-4 h-4" />
                )}

                {isEdit
                  ? "Reload"
                  : "Clear"}
              </button>

              <div className="flex gap-4">
                {/* BACK */}

                <button
                  type="button"
                  onClick={
                    onBack
                  }
                  disabled={
                    saving
                  }
                  className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                >
                  <FaArrowLeft className="w-4 h-4" />

                  Back
                </button>

                {/* SAVE / UPDATE */}

                <button
                  type="button"
                  onClick={
                    onSave
                  }
                  disabled={
                    saving ||
                    pageLoading ||
                    loadingDDL ||
                    !canSave
                  }
                  className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving ||
                    pageLoading ||
                    loadingDDL ||
                    !canSave
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />

                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />

                      {isEdit
                        ? "Update"
                        : "Save"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ==================================================
              FOOTER
              ================================================== */}

          <div
            className={`mt-8 pt-6 border-t ${
              isDark
                ? "border-gray-700"
                : "border-gray-200"
            }`}
          >
            <p
              className={`text-sm text-center ${
                isDark
                  ? "text-gray-400"
                  : "text-gray-500"
              }`}
            >
              INFRA SCREEN : BATTERY CARD MAINTENANCE ENTRY
            </p>
          </div>

          {/* ==================================================
              TOAST
              ================================================== */}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={
              isDark
                ? "dark"
                : "light"
            }
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardMaintenanceEntry;